import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Records a short voice clip from the browser mic and hands back a WAV File.
 *
 * WAV is not an arbitrary choice. Chrome's MediaRecorder produces
 * `audio/webm;codecs=opus`, and iOS plays alarm audio through AVAudioPlayer,
 * which supports neither WebM nor Opus — so a tone recorded in Chrome would be
 * silent on every iPhone, while the same feature used from Safari (which
 * records `audio/mp4`) would work. That is a browser-dependent failure nobody
 * would notice until a parent complained.
 *
 * So whatever the browser gives us is decoded to raw PCM and re-encoded as a
 * 16-bit mono WAV, which Android MediaPlayer, iOS AVAudioPlayer and every
 * browser can all play.
 */

export const MAX_RECORDING_SECONDS = 30;

// Voice only needs a fraction of CD quality. Mono at 22.05 kHz keeps a 30s
// clip near 1.3 MB — well under the form's 5 MB cap, and a smaller download
// for every device that caches it.
const TARGET_SAMPLE_RATE = 22050;

/** Mixes to mono and resamples with linear interpolation. */
function toMono(audioBuffer, targetRate) {
  const { numberOfChannels, length, sampleRate } = audioBuffer;

  const mixed = new Float32Array(length);
  for (let c = 0; c < numberOfChannels; c += 1) {
    const channel = audioBuffer.getChannelData(c);
    for (let i = 0; i < length; i += 1) mixed[i] += channel[i];
  }
  if (numberOfChannels > 1) {
    for (let i = 0; i < length; i += 1) mixed[i] /= numberOfChannels;
  }

  if (sampleRate === targetRate) return mixed;

  const ratio = sampleRate / targetRate;
  const outLength = Math.floor(length / ratio);
  const out = new Float32Array(outLength);
  for (let i = 0; i < outLength; i += 1) {
    const pos = i * ratio;
    const idx = Math.floor(pos);
    const frac = pos - idx;
    const a = mixed[idx] ?? 0;
    const b = mixed[idx + 1] ?? a;
    out[i] = a + (b - a) * frac;
  }
  return out;
}

/** Wraps mono Float32 samples in a 16-bit PCM RIFF/WAVE container. */
function encodeWav(samples, sampleRate) {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);

  const writeString = (offset, str) => {
    for (let i = 0; i < str.length; i += 1) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true); // PCM chunk size
  view.setUint16(20, 1, true); // format: PCM
  view.setUint16(22, 1, true); // channels: mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeString(36, "data");
  view.setUint32(40, samples.length * 2, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i += 1) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }

  return new Blob([view], { type: "audio/wav" });
}

const useVoiceRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { file, url }

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const resultUrlRef = useRef(null);

  const releaseStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Revoke the last object URL and drop the mic on unmount — a live mic
  // indicator left burning after the modal closes reads as spyware.
  useEffect(
    () => () => {
      releaseStream();
      if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current);
    },
    [releaseStream],
  );

  const reset = useCallback(() => {
    if (resultUrlRef.current) {
      URL.revokeObjectURL(resultUrlRef.current);
      resultUrlRef.current = null;
    }
    setResult(null);
    setSeconds(0);
    setError(null);
  }, []);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") recorder.stop();
  }, []);

  const start = useCallback(async () => {
    setError(null);
    reset();

    // getUserMedia only exists in a secure context. On a plain-HTTP staging
    // host the API is simply absent, which would otherwise look like a dead
    // button rather than a deployment problem.
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setError(
        window.isSecureContext === false
          ? "Recording needs a secure (https) connection. Open the portal over https and try again."
          : "This browser does not support recording. Try Chrome, Edge or Safari.",
      );
      return;
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      setError(
        err?.name === "NotAllowedError"
          ? "Microphone access was blocked. Allow it in your browser's site settings and try again."
          : "Could not start the microphone.",
      );
      return;
    }

    streamRef.current = stream;
    chunksRef.current = [];

    const recorder = new MediaRecorder(stream);
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      releaseStream();
      setIsRecording(false);
      setIsProcessing(true);

      try {
        const raw = new Blob(chunksRef.current, {
          type: recorder.mimeType || "audio/webm",
        });
        const arrayBuffer = await raw.arrayBuffer();

        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioCtx();
        let decoded;
        try {
          decoded = await ctx.decodeAudioData(arrayBuffer);
        } finally {
          ctx.close();
        }

        const mono = toMono(decoded, TARGET_SAMPLE_RATE);
        const wav = encodeWav(mono, TARGET_SAMPLE_RATE);

        const file = new File([wav], `alarm-voice-${Date.now()}.wav`, {
          type: "audio/wav",
        });
        const url = URL.createObjectURL(wav);
        resultUrlRef.current = url;
        setResult({ file, url });
      } catch (err) {
        setError("Could not process the recording. Please try again.");
      } finally {
        setIsProcessing(false);
      }
    };

    recorder.start();
    setIsRecording(true);
    setSeconds(0);

    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        const next = s + 1;
        // The tone loops, so extra length buys nothing and costs every device
        // a bigger download.
        if (next >= MAX_RECORDING_SECONDS) stop();
        return next;
      });
    }, 1000);
  }, [releaseStream, reset, stop]);

  return {
    isRecording,
    isProcessing,
    seconds,
    error,
    result,
    start,
    stop,
    reset,
  };
};

export default useVoiceRecorder;
